import { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import { toast } from 'sonner';
import type { SuggestedAction } from '../types/breaks';
import { PlatformGlyph } from './PlatformGlyph';
import {
  ACTION_PLATFORM_LABEL,
  PLATFORM_ICON_BG,
  PLATFORM_TEXT,
} from './break-styles';
import { GanttActionPanel } from './GanttActionPanel';

interface ActionPlanProps {
  /** Stable key (the break id) so applied state resets when the break changes. */
  breakId: string;
  actions: SuggestedAction[];
}

const PLAN_KEYFRAMES = `
@keyframes action-pop {
  0% { transform: scale(0.5); opacity: 0; }
  60% { transform: scale(1.18); }
  100% { transform: scale(1); opacity: 1; }
}`;

export function ActionPlan({ breakId, actions }: ActionPlanProps) {
  const [appliedIds, setAppliedIds] = useState<Set<string>>(() =>
    initialApplied(actions)
  );

  // Reset applied state whenever a different break is opened.
  useEffect(() => {
    setAppliedIds(initialApplied(actions));
  }, [breakId, actions]);

  const appliedCount = actions.filter((action) =>
    appliedIds.has(action.id)
  ).length;
  const allApplied = appliedCount === actions.length;
  const toolCount = new Set(actions.map((action) => action.platform)).size;
  const toolWord = toolCount === 1 ? 'tool' : 'tools';

  // The schedule-bearing action becomes the Gantt command center; the rest are
  // the coordinated follow-up steps it triggers across the other tools.
  const scheduleAction = actions.find((action) => action.gantt);
  const otherActions = actions.filter((action) => action !== scheduleAction);

  const applyAction = (action: SuggestedAction) => {
    setAppliedIds((current) => {
      const next = new Set(current);
      next.add(action.id);
      return next;
    });
    toast.success(action.appliedLabel, {
      description: 'Intera applied this action for you.',
    });
  };

  const applyAll = () => {
    setAppliedIds(new Set(actions.map((action) => action.id)));
    toast.success('Plan applied', {
      description: `Intera ran all ${actions.length} actions for you.`,
    });
  };

  // Timeline-as-command-center: the Gantt hosts the entire plan - the schedule
  // change, the inline per-row actions, and the coordinated follow-ups.
  if (scheduleAction?.gantt) {
    return (
      <GanttActionPanel
        plan={scheduleAction.gantt}
        scheduleApplied={appliedIds.has(scheduleAction.id)}
        onApplySchedule={() => applyAction(scheduleAction)}
        coordinatedActions={otherActions}
        appliedActionIds={appliedIds}
        onApplyAction={applyAction}
        onApplyAll={applyAll}
        allApplied={allApplied}
      />
    );
  }

  // Fallback for breaks with no schedule Gantt: the coordinated-action list.
  return (
    <div>
      <style>{PLAN_KEYFRAMES}</style>

      {/* Hero: resolve the whole plan in one click (flips to a success banner) */}
      <div
        className={`flex items-center justify-between gap-4 rounded-2xl border px-5 py-4 transition-colors ${
          allApplied
            ? 'border-emerald-200 bg-emerald-50/60'
            : 'border-zinc-200 bg-zinc-50/70'
        }`}
      >
        <div className="flex items-center gap-3">
          <span
            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${
              allApplied
                ? 'bg-emerald-500 text-white'
                : 'bg-white ring-1 ring-inset ring-zinc-200'
            }`}
          >
            {allApplied ? (
              <Check
                className="h-5 w-5"
                strokeWidth={3}
                style={{ animation: 'action-pop 0.4s ease-out' }}
              />
            ) : (
              <PlatformGlyph platform="intera" className="h-5 w-5" />
            )}
          </span>
          <div>
            <p className="text-sm font-semibold text-zinc-900">
              {allApplied
                ? 'Plan applied - Intera handled it'
                : 'Resolve the whole gap in one click'}
            </p>
            <p className="mt-0.5 text-[13px] text-zinc-500">
              {allApplied
                ? `${actions.length} actions ran across ${toolCount} ${toolWord}.`
                : `Intera runs ${actions.length} coordinated actions across ${toolCount} ${toolWord}.`}
            </p>
          </div>
        </div>
        {!allApplied && (
          <button
            type="button"
            onClick={applyAll}
            className="shrink-0 whitespace-nowrap rounded-full bg-button-primary px-5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-button-primary-hover active:scale-95"
          >
            Apply all {actions.length}
          </button>
        )}
      </div>

      {/* The coordinated steps - the spine fills green as steps land */}
      <p className="mb-3 mt-7 text-[11px] font-semibold uppercase tracking-widest text-zinc-400">
        Coordinated actions
      </p>
      <ol>
        {otherActions.map((action, index) => {
          const isApplied = appliedIds.has(action.id);
          const isLast = index === otherActions.length - 1;
          return (
            <li key={action.id} className="relative flex gap-4">
              {/* Spine: numbered node + connector to the next step */}
              <div className="relative flex w-8 shrink-0 flex-col items-center">
                <span
                  className={`z-10 flex h-8 w-8 items-center justify-center rounded-full text-[13px] font-bold transition-colors ${
                    isApplied
                      ? 'bg-emerald-500 text-white shadow-sm'
                      : 'border-2 border-zinc-300 bg-white text-zinc-500'
                  }`}
                >
                  {isApplied ? (
                    <Check
                      className="h-4 w-4"
                      strokeWidth={3}
                      style={{ animation: 'action-pop 0.4s ease-out' }}
                    />
                  ) : (
                    index + 1
                  )}
                </span>
                {!isLast && (
                  <span
                    className={`w-0.5 flex-1 transition-colors ${
                      isApplied ? 'bg-emerald-300' : 'bg-zinc-200'
                    }`}
                  />
                )}
              </div>

              {/* Action card */}
              <div
                className={`mb-3 min-w-0 flex-1 rounded-2xl border p-4 transition-colors ${
                  isApplied
                    ? 'border-emerald-200 bg-emerald-50/40'
                    : 'border-zinc-200 bg-white hover:border-zinc-300'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    {/* Which tool this action runs in */}
                    <div className="mb-1.5 inline-flex items-center gap-1.5">
                      <span
                        className={`flex h-6 w-6 items-center justify-center rounded-md ${
                          PLATFORM_ICON_BG[action.platform]
                        }`}
                      >
                        <PlatformGlyph
                          platform={action.platform}
                          className={`h-3.5 w-3.5 ${
                            PLATFORM_TEXT[action.platform]
                          }`}
                        />
                      </span>
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400">
                        Runs in {ACTION_PLATFORM_LABEL[action.platform]}
                      </span>
                    </div>
                    <p className="text-[15px] font-semibold leading-snug text-zinc-900">
                      {action.title}
                    </p>
                    <p className="mt-0.5 text-[13px] leading-relaxed text-zinc-500">
                      {action.detail}
                    </p>
                  </div>

                  {isApplied ? (
                    <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-emerald-100 px-3 py-1.5 text-xs font-bold text-emerald-700">
                      <Check className="h-3.5 w-3.5" strokeWidth={3} />
                      {action.appliedLabel}
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => applyAction(action)}
                      className="shrink-0 whitespace-nowrap rounded-full border border-button-primary bg-white px-4 py-1.5 text-xs font-semibold text-button-primary transition-colors hover:bg-button-primary hover:text-white active:scale-95"
                    >
                      {action.actionLabel}
                    </button>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function initialApplied(actions: SuggestedAction[]): Set<string> {
  return new Set(
    actions.filter((action) => action.preApplied).map((action) => action.id)
  );
}
